import { motion } from "framer-motion";
import React, { memo, useCallback, useMemo, useState } from "react";
import VizSensor from "react-visibility-sensor";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import anim from "../../utils/anim";

import accessibilityProvider from "../../utils/accessibilityProvider";
import getImgUrl from "../../utils/getImgUrl";

import useData from "../../hooks/useData";
import getParsedRedirectUrl from "../../utils/getParsedRedirectUrl";
import productEventPropertyMapper from "../../utils/productEventPropertyMapper";
import "./product-card.css";
import useCachedViewTrack, {
	TRACKED_TARGET,
} from "../../hooks/useCachedViewTrack";
import ProductCardDetails from "./ProductCardDetails";
import useImpression from "../../hooks/useImpression";
import { SNOWPLOW_EVENT } from "../../utils/snowplowEventMigrator";
import linkProvider from "../../utils/linkProvider";
import useExperience from "../../hooks/useExperience";
import { WIDGET_TYPE } from "../../conf";
import { PRODUCT_CLICK_TARGET } from "../../event_conf";
import { genClassName } from "@disco/disco_core";
import EngageSecondaryTile from "../EngageWidget/EngageSecondaryTile";

export const generateProductUid = (product = {}) => {
	if (!product?.id) return null;
	const {
		id: productId,
		is_variant: isProductVariant,
		variant_remote_id: productVariantId,
	} = product;

	return isProductVariant ? productVariantId : productId;
};

const ProductCard = memo(
	({
		product,
		idx,
		parentBrand,
		parentBrandRef,
		parentBrandIdx,
		isParentBrandInView,
		upsell = false,
		disableAnimation = false,
		onDisplayTrack,
		onThresholdDisplayTrack,
	}) => {
		const track = useTrack();
		const [inView, setInView] = useState(false);
		const [productRef, setProductRef] = useState(false);

		const { feedHasUpsell } = useData();

		const {
			name,
			id: productId,
			masked_url: productUrl,
		} = useMemo(() => product || {}, [product]);

		const { activeWidgetType } = useExperience();
		const eventInitiatingTarget =
			activeWidgetType === WIDGET_TYPE.DISCOFEED
				? PRODUCT_CLICK_TARGET.DISCO_FEED_1
				: PRODUCT_CLICK_TARGET.BOTTOM_SLOT;

		const defaultEventProps = useMemo(
			() => ({
				...productEventPropertyMapper({
					brand: parentBrand,
					brandIdx: parentBrandIdx,
					idx,
					product,
					upsell,
					feedHasUpsell,
				}),
				initiatingTarget: eventInitiatingTarget,
				brand_view_order: idx, // manual override to stay consistent with legacy events
			}),
			[
				parentBrand,
				feedHasUpsell,
				parentBrandIdx,
				product,
				idx,
				upsell,
				eventInitiatingTarget,
			]
		);

		const productRedirectUrl = getParsedRedirectUrl({
			redirectUrl: productUrl,
			brand: parentBrand,
			upsell,
		});

		const handleClick = useCallback(() => {
			track(eventTypes.CLICK, {
				...defaultEventProps,
				product_redirect_link: productRedirectUrl,
			});
		}, [defaultEventProps, track, productRedirectUrl]);

		const handleVizChange = useCallback(
			(isVisible) => setInView(isVisible),
			[]
		);

		const { hasBeenTracked } = useCachedViewTrack({
			id: productId,
			key: TRACKED_TARGET.PRODUCT_CARD,
			event: SNOWPLOW_EVENT.WIDGET_PRODUCT_DISPLAY,
			properties: defaultEventProps,
			canTrack: isParentBrandInView && inView,
			onTrack: onDisplayTrack,
		});

		// TODO: Think about using ref ref instead of state ref
		const productUid = useMemo(
			() => generateProductUid(product),
			[product]
		);

		useImpression({
			id: productUid,
			cacheKey: TRACKED_TARGET.VIEWABLE_THRESHOLD_PRODUCT,
			event: SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_PRODUCT_DISPLAY,
			properties: defaultEventProps,

			target: productRef,
			callback: onThresholdDisplayTrack,
		});

		if (!product || !parentBrandRef) {
			return null;
		}

		return (
			<VizSensor
				active={!hasBeenTracked}
				onChange={handleVizChange}
				containment={parentBrandRef}
				partialVisibility
				offset={{ right: 30 }}
			>
				<motion.a
					className={genClassName({
						base: "widget-product-card",
						conditionals: {
							"widget-engage-product-card":
								activeWidgetType === WIDGET_TYPE.ENGAGE,
						},
					})}
					data-testid="widget-product-card"
					data-intersectionuid={productUid}
					onClick={handleClick}
					// Conditionally add variant for only first time display
					variants={!disableAnimation && anim.card}
					initial="initial"
					animate="animate"
					ref={(productRef) => setProductRef(productRef)}
					{...accessibilityProvider({
						role: "link",
						name: `Product: ${name}`,
					})}
					{...linkProvider({
						href: productRedirectUrl,
						injectElement: false,
					})}
				>
					{activeWidgetType === WIDGET_TYPE.ENGAGE ? (
						<EngageSecondaryTile
							product={product}
							brand={parentBrand}
						/>
					) : (
						<>
							<img
								src={getImgUrl(product)}
								className="widget-product-image"
								alt={`${name}`}
							/>
							<ProductCardDetails
								product={product}
								brand={parentBrand}
								prefixBrandName={
									activeWidgetType === WIDGET_TYPE.LEAD_GEN
								}
							/>
						</>
					)}
				</motion.a>
			</VizSensor>
		);
	}
);

export default ProductCard;
